import events from "add-event-listener";
import debounce from "lodash.debounce";
import throttle from "lodash.throttle";
import PropTypes from "prop-types";
import * as React from "react";

import { scrollLeft, scrollRight } from "../../MultiControls/StickyTable/scroll";
import { ArrowLeft } from "../Arrows/ArrowLeft";
import { ArrowRight } from "../Arrows/ArrowRight";
import { IScrollToContext, scrollToContextTypes, ScrollToProvider } from "../Contexts/ScrollToContext";
import { pullContext } from "../../TypingUtils";

import {
    Arrow,
    arrowVisibleClassName,
    ScrollableContainer2Root,
    Root,
    ScrollBar,
    ScrollbarChild,
} from "./ScrollableContainer2.styles";

export interface ScrollableContainerProps {
    className?: string;
    children?: undefined | React.ReactElement<{ className: undefined | string }>;
}

type ScrollingSource = "none" | "container" | "scrollbar";

interface Margins {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

const arrowMargins: Margins = {
    left: 50,
    right: 50,
    top: 50,
    bottom: 50,
};

export interface ScrollableContainerChildContext {
    getScrollLeft: () => number;
    onScrollContainerWidthChange: (handler: (width: number) => void) => void;
}

export class ScrollableContainer2 extends React.Component<ScrollableContainerProps> {
    public static contextTypes = {
        ...scrollToContextTypes,
    };

    public static childContextTypes = {
        getScrollLeft: PropTypes.func,
        onScrollContainerWidthChange: PropTypes.func,
    };

    private scrollLeft = 0;
    private width = 0;
    private unitLeft?: number;
    private scrollingSource: ScrollingSource = "none";
    private isFixedScrollBarVisible?: boolean;
    private resizeInterval?: number;
    private handlers: Array<(width: number) => void> = [];

    private readonly root = React.createRef<HTMLDivElement>();
    private readonly container = React.createRef<HTMLDivElement>();
    private readonly scrollbarChild = React.createRef<HTMLDivElement>();
    private readonly scrollbar = React.createRef<HTMLDivElement>();
    private readonly leftArrow = React.createRef<HTMLDivElement>();
    private readonly rightArrow = React.createRef<HTMLDivElement>();

    private readonly setScrollingSource = debounce((x: ScrollingSource) => {
        this.scrollingSource = x;
    }, 50);

    public getChildContext(): ScrollableContainerChildContext {
        return {
            getScrollLeft: () => this.scrollLeft,
            onScrollContainerWidthChange: (handler: (width: number) => void) => this.handlers.push(handler),
        };
    }

    public getContext(): IScrollToContext {
        return pullContext<IScrollToContext>(this);
    }

    public componentDidMount(): void {
        this.adjustPositions();
        this.init();
        events.addEventListener(window, "scroll", this.handlePageScroll);
        events.addEventListener(window, "resize", this.handlePageResize);
        this.resizeInterval = window.setInterval(this.resizeHandler, 100);
        const left = this.root.current?.getBoundingClientRect().left;

        this.unitLeft = left;
    }

    public componentWillUnmount(): void {
        events.removeEventListener(window, "resize", this.handlePageResize);
        events.removeEventListener(window, "scroll", this.handlePageScroll);

        if (this.resizeInterval) {
            window.clearInterval(this.resizeInterval);
        }

        this.handlers = [];
    }

    public render(): JSX.Element {
        const { children } = this.props;

        return (
            <ScrollToProvider value={this.scrollTo}>
                <ScrollableContainer2Root ref={this.root}>
                    <Root ref={this.container} className={this.props.className}>
                        {children}
                    </Root>
                    <ScrollBar ref={this.scrollbar}>
                        <ScrollbarChild ref={this.scrollbarChild} />
                    </ScrollBar>
                    <Arrow ref={this.leftArrow} onClick={this.handleLeftArrowClick}>
                        <ArrowLeft />
                    </Arrow>
                    <Arrow ref={this.rightArrow} onClick={this.handleRightArrowClick}>
                        <ArrowRight />
                    </Arrow>
                </ScrollableContainer2Root>
            </ScrollToProvider>
        );
    }

    public setScrollPosition(value: number): void {
        if (this.container.current != undefined) {
            this.container.current.scrollLeft = value;
        }
    }

    public componentDidUpdate(): void {
        this.adjustArrowsVisibility();
    }

    private readonly scrollTo = (component: Element) => {
        if (this.container.current == undefined) {
            return;
        }
        this.getContext().scrollTo?.(component);
        const containerRect = this.container.current.getBoundingClientRect();
        const componentRect = component.getBoundingClientRect();

        if (componentRect.right > containerRect.right) {
            this.container.current.scrollLeft = componentRect.right - containerRect.right + 20;
        } else if (componentRect.left < containerRect.left) {
            this.container.current.scrollLeft -= containerRect.left - componentRect.left + 20;
        }
    };

    private readonly resizeHandler = () => {
        if (this.root.current) {
            const { width, left } = this.root.current.getBoundingClientRect();

            if (this.width !== width || this.unitLeft !== left) {
                this.width = width;
                this.unitLeft = left;
                this.adjustArrowsVisibility();
                this.adjustPositions();
                this.handlers.forEach(handler => handler(width));
            }
        }
    };

    private readonly handleLeftArrowClick = () => {
        if (this.container.current != undefined) {
            const offset = this.container.current.clientWidth * 0.66;
            scrollLeft(this.container.current, offset);
        }
    };

    private readonly handleRightArrowClick = () => {
        if (this.container.current != undefined) {
            const offset = this.container.current.clientWidth * 0.66;
            scrollRight(this.container.current, offset);
        }
    };

    private adjustPositions(): void {
        const currentScrollLeft = this.scrollLeft;
        const container = this.container.current;
        const scrollbar = this.scrollbar.current;
        const scrollbarChild = this.scrollbarChild.current;

        if (this.container.current == undefined) {
            return;
        } else {
            if (this.container.current.scrollWidth <= this.container.current.clientWidth) {
                this.hideFixedScrollbar();
                return;
            }
        }

        if (container != undefined && scrollbar != undefined && scrollbarChild != undefined) {
            const containerRect = container.getBoundingClientRect();

            // adjust sticky scrollbar
            if (containerRect.top < window.innerHeight - 40 && containerRect.bottom > window.innerHeight) {
                this.showFixedScrollbar();
                scrollbarChild.style.width = `${container.scrollWidth}px`;
                scrollbar.style.left = `${containerRect.left}px`;
                scrollbar.style.width = `${this.width}px`;
                scrollbar.scrollLeft = currentScrollLeft;
            } else {
                this.hideFixedScrollbar();
            }

            // adjust left arrow position
            const leftArrow = this.leftArrow.current;
            if (leftArrow != undefined) {
                const leftArrowRect = leftArrow.getBoundingClientRect();
                if (
                    containerRect.top + arrowMargins.top < window.innerHeight / 2 &&
                    containerRect.bottom - leftArrowRect.height - arrowMargins.bottom > window.innerHeight / 2
                ) {
                    leftArrow.style.position = "fixed";
                    leftArrow.style.left = `${containerRect.left + arrowMargins.left}px`;
                    leftArrow.style.top = `${window.innerHeight / 2}px`;
                    leftArrow.style.bottom = "";
                } else {
                    leftArrow.style.position = "absolute";
                    leftArrow.style.left = `${arrowMargins.left}px`;
                    if (containerRect.top + arrowMargins.top >= window.innerHeight / 2) {
                        leftArrow.style.bottom = "";
                        leftArrow.style.top = `${arrowMargins.top}px`;
                    } else if (
                        containerRect.bottom - leftArrowRect.height - arrowMargins.bottom <=
                        window.innerHeight / 2
                    ) {
                        leftArrow.style.bottom = `${arrowMargins.bottom}px`;
                        leftArrow.style.top = "";
                    }
                }
            }

            // adjust right arrow position
            const rightArrow = this.rightArrow.current;
            if (rightArrow != undefined) {
                const rightArrowRect = rightArrow.getBoundingClientRect();
                if (
                    containerRect.top + arrowMargins.top < window.innerHeight / 2 &&
                    containerRect.bottom - rightArrowRect.height - arrowMargins.bottom > window.innerHeight / 2
                ) {
                    rightArrow.style.position = "fixed";
                    rightArrow.style.left = `${containerRect.right - rightArrowRect.width - arrowMargins.right}px`;
                    rightArrow.style.right = "";
                    rightArrow.style.top = `${window.innerHeight / 2}px`;
                    rightArrow.style.bottom = "";
                } else {
                    rightArrow.style.position = "absolute";
                    rightArrow.style.left = "";
                    rightArrow.style.right = `${arrowMargins.right}px`;
                    if (containerRect.top + arrowMargins.top >= window.innerHeight / 2) {
                        rightArrow.style.bottom = "";
                        rightArrow.style.top = `${arrowMargins.top}px`;
                    } else if (
                        containerRect.bottom - rightArrowRect.height - arrowMargins.bottom <=
                        window.innerHeight / 2
                    ) {
                        rightArrow.style.bottom = `${arrowMargins.bottom}px`;
                        rightArrow.style.top = "";
                    }
                }
            }
        }
    }

    private readonly handlePageScroll = () => {
        this.adjustPositions();
        this.adjustArrowsVisibility();
    };

    private readonly handlePageResize = () => {
        this.adjustPositions();
        this.adjustArrowsVisibility();
    };

    private hideFixedScrollbar(): void {
        if (this.isFixedScrollBarVisible == undefined || this.isFixedScrollBarVisible) {
            this.isFixedScrollBarVisible = false;
            const scrollbar = this.scrollbar.current;
            if (scrollbar != undefined) {
                scrollbar.style.display = "none";
            }
        }
    }

    private showFixedScrollbar(): void {
        if (this.isFixedScrollBarVisible == undefined || !this.isFixedScrollBarVisible) {
            this.isFixedScrollBarVisible = true;
            const scrollbar = this.scrollbar.current;
            if (scrollbar != undefined) {
                scrollbar.style.display = "block";
            }
        }
    }

    private adjustArrowsVisibility(): void {
        const container = this.container.current;
        const leftArrow = this.leftArrow.current;
        if (container == undefined) {
            return;
        }
        if (leftArrow != undefined) {
            if (this.scrollLeft > 0) {
                leftArrow.classList.add(arrowVisibleClassName);
            } else {
                leftArrow.classList.remove(arrowVisibleClassName);
            }
        }
        const rightArrow = this.rightArrow.current;
        if (rightArrow != undefined) {
            if (container.scrollLeft < container.scrollWidth - container.offsetWidth - 5) {
                rightArrow.classList.add(arrowVisibleClassName);
            } else {
                rightArrow.classList.remove(arrowVisibleClassName);
            }
        }
    }

    private init(): void {
        const scrollbar = this.scrollbar.current;
        const container = this.container.current;
        if (scrollbar == undefined || container == undefined) {
            return;
        }

        scrollbar.addEventListener(
            "scroll",
            throttle(() => {
                if (this.scrollingSource === "scrollbar" || this.scrollingSource === "none") {
                    this.scrollingSource = "scrollbar";
                    container.scrollLeft = scrollbar.scrollLeft;
                    this.setScrollingSource("none");
                } else {
                    this.scrollLeft = scrollbar.scrollLeft;
                }
                this.adjustArrowsVisibility();
                this.handlers.forEach(handler => handler(this.width));
            }, 50)
        );

        container.addEventListener(
            "scroll",
            throttle(() => {
                if (this.scrollingSource === "container" || this.scrollingSource === "none") {
                    this.scrollingSource = "container";
                    scrollbar.scrollLeft = container.scrollLeft;
                    this.scrollLeft = container.scrollLeft;
                    this.setScrollingSource("none");
                } else {
                    this.scrollLeft = container.scrollLeft;
                }
                this.adjustArrowsVisibility();
                this.handlers.forEach(handler => handler(this.width));
            }, 50)
        );
    }
}
